import { ExpiredAltIdMatchReviewsQuery } from "generated-graphql/graphql";
import { DateTime } from "luxon";

export type ReviewRow = ExpiredAltIdMatchReviewsQuery["expiredAltIdMatchReviews"]["items"][number];

/** From pending, accept or reject; from a decided state, reverse it. */
export type ReviewDecision = "accept" | "unaccept" | "reject" | "unreject";

/** The row's decision state, from its two mutually-exclusive timestamps. */
export function reviewDecisionState(
  row: Pick<ReviewRow, "acceptedAt" | "rejectedAt">
): "pending" | "accepted" | "rejected" {
  if (row.acceptedAt) return "accepted";
  if (row.rejectedAt) return "rejected";
  return "pending";
}

/**
 * FRS_REGISTRY covers water AND air, because both match an FRS-typed id against
 * the violation's registry id. Accepting such a group releases both programs'
 * rows for the facility — a property of how EPA identifies facilities, so the
 * label says so rather than implying a single program. SHARED_FRS is the
 * cross-program (prior owner / shared site) path, with its own acceptance.
 * A Record so widening the kind enum is a compile error here, not a silent
 * fall-through label.
 */
const MATCH_KIND_LABEL: Record<ReviewRow["matchKind"], string> = {
  WASTE_HANDLER: "Waste handler ID",
  FRS_REGISTRY: "FRS registry ID (water + air)",
  SHARED_FRS: "Shared FRS registry (prior owner)",
};

export function matchKindLabel(matchKind: ReviewRow["matchKind"]): string {
  return MATCH_KIND_LABEL[matchKind];
}

/**
 * Read in UTC, deliberately.
 *
 * These values are dates wearing timestamps: `expiresAt` is typed into a date
 * picker and stored at UTC midnight, and the Date-scalar violation dates arrive
 * as bare `YYYY-MM-DD`. Rendering either through the viewer's local zone puts a
 * US reader a day early on every expiry — the exact field staff are judging.
 * `firstSeenAt` is a real run timestamp and rides along; being off by hours on a
 * "first seen" column matters far less than being off by a day on an expiry.
 */
export function formatDate(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const parsed = DateTime.fromISO(value, { zone: "utc" });
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : "—";
}

/**
 * The expiry behind the group. A waste group can rest on two rows with
 * independent dates, so this joins rather than picking one.
 *
 * The empty case — a group whose ID rows were deleted outright rather than
 * expired, leaving it orphaned but still held — is a state, not a date, so the
 * cell renders a chip for it instead of calling this.
 */
export function expiryDisplay(alternateIds: ReviewRow["alternateIds"]): string {
  return alternateIds
    .map((alternateId) => `${alternateId.type}: ${formatDate(alternateId.expiresAt)}`)
    .join(" · ");
}

/**
 * The span the group's violations cover. Collapses to a single date when they
 * all fall on one day, which is common — a range that repeats the same date
 * twice reads as a bug and costs the column twice the width it needs.
 */
export function violationDateRange(row: ReviewRow): string {
  const { earliestViolationStartDate: earliest, latestViolationStartDate: latest } = row;
  if (!earliest && !latest) {
    return "—";
  }
  if (!earliest || !latest || earliest === latest) {
    return formatDate(earliest ?? latest);
  }
  return `${formatDate(earliest)} – ${formatDate(latest)}`;
}
